
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Code } from 'lucide-react';
import { FeedbackRequest, FeedbackResponse } from '@/types/feedback';

interface FeedbackSuccessProps {
  requestData: FeedbackRequest;
  responseData: FeedbackResponse;
  onReset: () => void;
}

export const FeedbackSuccess = ({ requestData, responseData, onReset }: FeedbackSuccessProps) => {
  const formatDate = (isoString: string) => {
    // Debug logging in development
    if (import.meta.env.MODE === 'development') {
      console.log('formatDate input:', isoString, typeof isoString);
    }
    
    if (!isoString) {
      return 'Just now';
    }
    
    try {
      const date = new Date(isoString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        if (import.meta.env.MODE === 'development') {
          console.log('Invalid date created from:', isoString);
        }
        return 'Just now';
      }
      
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      if (import.meta.env.MODE === 'development') {
        console.log('Date formatting error:', error);
      }
      return 'Just now';
    }
  };

  const isDevelopment = import.meta.env.MODE === 'development';

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-4xl space-y-6">
        <Card className="shadow-2xl border-border/70 bg-card/90 backdrop-blur-sm">
          <CardHeader className="text-center pb-4">
            <div className="mx-auto mb-4 w-16 h-16 bg-secondary rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="text-2xl font-bold text-foreground">
              Feedback Submitted Successfully!
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              {isDevelopment 
                ? "Here's the API communication that occurred" 
                : "Thank you for sharing your valuable feedback with us"
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {isDevelopment && (
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Code className="w-5 h-5 text-primary" />
                    <h3 className="text-lg font-semibold text-foreground">Request Sent</h3>
                  </div>
                  <div className="bg-muted rounded-lg p-4 border">
                    <p className="text-sm font-medium text-muted-foreground mb-2">POST /feedback</p>
                    <pre className="text-sm text-foreground overflow-x-auto">
{JSON.stringify(requestData, null, 2)}
                    </pre>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-primary" />
                    <h3 className="text-lg font-semibold text-foreground">Response Received</h3>
                  </div>
                  <div className="bg-secondary/60 rounded-lg p-4 border border-border">
                    <p className="text-sm font-medium text-primary mb-2">Status: 200 OK</p>
                    <pre className="text-sm text-foreground overflow-x-auto">
{JSON.stringify(responseData, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
            )}

            {!isDevelopment && (
              <div className="bg-secondary/60 rounded-lg p-6 border border-border text-center">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <CheckCircle className="w-6 h-6 text-primary" />
                  <h3 className="text-lg font-semibold text-foreground">Feedback Received</h3>
                </div>
                <p className="text-foreground mb-4">
                  Your feedback has been successfully submitted and will help us improve our products and services.
                </p>
                <p className="text-sm text-muted-foreground">
                  We appreciate you taking the time to share your thoughts with us.
                </p>
              </div>
            )}

            <div className="bg-muted rounded-lg p-4 space-y-2 text-center">
              <p className="text-sm text-muted-foreground">
                {isDevelopment ? "Feedback ID" : "Reference ID"}
              </p>
              <p className="font-mono text-sm font-medium text-foreground">{responseData.id}</p>
            </div>

            <Button
              onClick={onReset}
              className="w-full mt-6 bg-gradient-to-r from-primary via-brand-coral to-brand-amber hover:from-primary/90 hover:via-brand-coral/90 hover:to-brand-amber/90 text-primary-foreground"
            >
              Submit Another Feedback
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
